// Quote text + PDF utilities (extracted from QuoteDetailPage for maintainability).
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const computeTotal = (quote) => {
  const items = quote.generated?.items || [];
  return items.length
    ? items.reduce((s, it) => s + Number(it.price || 0), 0)
    : Number(quote.total_price || 0);
};

export function quoteToPlainText(quote) {
  const g = quote.generated || {};
  const lines = [];
  lines.push(`${quote.company?.company_name || ""}`);
  lines.push(`${quote.company?.phone || ""} | ${quote.company?.email || ""} | ${quote.company?.address || ""}`);
  lines.push("");
  lines.push(`${quote.quote_type.toUpperCase()} — ${new Date(quote.created_at).toLocaleDateString()}`);
  lines.push(`For: ${quote.customer?.name || ""}`);
  lines.push(`Job address: ${quote.customer?.address || ""}`);
  lines.push("");
  lines.push(`PROJECT: ${g.project_title || ""}`);
  lines.push("");
  lines.push("SCOPE OF WORK");
  lines.push(g.scope_of_work || "");
  if ((g.items || []).length) {
    lines.push("");
    lines.push("ITEMIZED BREAKDOWN");
    g.items.forEach((it, i) => lines.push(`${i + 1}. ${it.description}  —  $${Number(it.price).toFixed(2)}`));
  }
  lines.push("");
  lines.push(`TOTAL: $${computeTotal(quote).toFixed(2)}`);
  if ((g.notes || []).length) {
    lines.push("");
    lines.push("NOTES");
    g.notes.forEach((n) => lines.push(`- ${n}`));
  }
  if (g.customer_message) {
    lines.push("");
    lines.push(g.customer_message);
  }
  return lines.join("\n");
}

// --- PDF helpers (each handles one section of the document) ---

function drawHeader(doc, quote, logoDataUrl, margin) {
  let y = margin;
  let textX = margin;
  if (logoDataUrl) {
    try {
      const fmt = logoDataUrl.includes("image/png") ? "PNG" : "JPEG";
      doc.addImage(logoDataUrl, fmt, margin, y - 4, 48, 48);
      textX = margin + 60;
    } catch (e) {
      console.warn("PDF logo embed failed:", e);
    }
  }
  doc.setFont("helvetica", "bold").setFontSize(20).setTextColor(15, 23, 42);
  doc.text(quote.company?.company_name || "Your Company", textX, y + 6);
  y += 24;
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(100);
  const companyLine = [quote.company?.phone, quote.company?.email, quote.company?.address].filter(Boolean).join("  ·  ");
  doc.text(companyLine, textX, y);
  return Math.max(y + 8, margin + 56);
}

function drawMeta(doc, quote, pageW, margin, y) {
  doc.setFontSize(10).setTextColor(120);
  doc.text(quote.quote_type.toUpperCase(), pageW - margin, margin, { align: "right" });
  doc.text(new Date(quote.created_at).toLocaleDateString(), pageW - margin, margin + 14, { align: "right" });
  doc.setDrawColor(226, 232, 240).line(margin, y, pageW - margin, y);
  return y + 20;
}

function drawCustomerBlock(doc, quote, pageW, margin, y) {
  doc.setFontSize(10).setTextColor(120).text("QUOTE FOR", margin, y);
  doc.text("JOB ADDRESS", pageW / 2, y);
  y += 14;
  doc.setFontSize(11).setTextColor(15, 23, 42);
  doc.text(quote.customer?.name || "—", margin, y);
  doc.text(quote.customer?.address || "—", pageW / 2, y);
  y += 14;
  doc.setFontSize(10).setTextColor(80);
  doc.text(quote.customer?.phone || "", margin, y);
  y += 12;
  doc.text(quote.customer?.email || "", margin, y);
  y += 20;
  doc.setDrawColor(226, 232, 240).line(margin, y, pageW - margin, y);
  return y + 18;
}

function drawScope(doc, g, pageW, margin, y) {
  doc.setFont("helvetica", "bold").setFontSize(16).setTextColor(15, 23, 42);
  doc.text(g.project_title || "Project", margin, y);
  y += 22;
  doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(120);
  doc.text("SCOPE OF WORK", margin, y);
  y += 14;
  doc.setFont("helvetica", "normal").setFontSize(11).setTextColor(40);
  const scopeLines = doc.splitTextToSize(g.scope_of_work || "", pageW - margin * 2);
  doc.text(scopeLines, margin, y);
  return y + scopeLines.length * 14 + 10;
}

function drawItems(doc, g, margin, y) {
  if (!(g.items || []).length) return y;
  autoTable(doc, {
    startY: y,
    head: [["#", "Description", "Price"]],
    body: g.items.map((it, i) => [i + 1, it.description, `$${Number(it.price).toFixed(2)}`]),
    theme: "grid",
    headStyles: { fillColor: [15, 23, 42], textColor: 255 },
    styles: { fontSize: 10 },
    columnStyles: { 0: { cellWidth: 30 }, 2: { halign: "right", cellWidth: 80 } },
    margin: { left: margin, right: margin },
  });
  return doc.lastAutoTable.finalY + 16;
}

function drawNotes(doc, g, pageW, margin, y) {
  if (!(g.notes || []).length) return y;
  doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(120);
  doc.text("NOTES", margin, y); y += 14;
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(60);
  g.notes.forEach((n) => {
    const lines = doc.splitTextToSize(`• ${n}`, pageW - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 12 + 2;
  });
  return y + 8;
}

function drawSignature(doc, pageW, margin, y) {
  doc.setDrawColor(200).line(margin, y + 10, pageW / 2 - 20, y + 10);
  doc.setFontSize(9).setTextColor(120).text("Customer Approval", margin, y + 24);
  doc.line(pageW / 2 + 20, y + 10, pageW - margin, y + 10);
  doc.text("Date", pageW / 2 + 20, y + 24);
}

export function exportQuotePdf(quote, logoDataUrl) {
  const doc = buildQuotePdfDoc(quote, logoDataUrl);
  const safeTitle = ((quote.generated || {}).project_title || "quote").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`scopewriter-${safeTitle}.pdf`);
}

/** Returns the PDF as a base64 string (no data URL prefix). */
export function quotePdfBase64(quote, logoDataUrl) {
  const doc = buildQuotePdfDoc(quote, logoDataUrl);
  // jsPDF returns a data URL like 'data:application/pdf;base64,XXXX'
  const dataUrl = doc.output("datauristring");
  const idx = dataUrl.indexOf("base64,");
  return idx >= 0 ? dataUrl.slice(idx + 7) : dataUrl;
}

function buildQuotePdfDoc(quote, logoDataUrl) {
  const g = quote.generated || {};
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 48;

  let y = drawHeader(doc, quote, logoDataUrl, margin);
  y = drawMeta(doc, quote, pageW, margin, y);
  y = drawCustomerBlock(doc, quote, pageW, margin, y);
  y = drawScope(doc, g, pageW, margin, y);
  y = drawItems(doc, g, margin, y);

  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(15, 23, 42);
  doc.text(`Total: $${computeTotal(quote).toFixed(2)}`, pageW - margin, y, { align: "right" });
  y += 24;

  y = drawNotes(doc, g, pageW, margin, y);

  if (g.customer_message) {
    doc.setFont("helvetica", "italic").setFontSize(10).setTextColor(80);
    const msgLines = doc.splitTextToSize(g.customer_message, pageW - margin * 2);
    doc.text(msgLines, margin, y);
    y += msgLines.length * 12 + 16;
  }

  drawSignature(doc, pageW, margin, y);
  return doc;
}
